/**
 * Maps Quote Builder PRO documents ↔ Budget Engine documents.
 * Projection only — no business pricing rules beyond field mapping.
 */

import type {
  BudgetDocument,
  BudgetLine,
  BudgetLineType,
  BudgetStatus,
  BudgetTotals,
} from '@/engines/budget'
import type {
  QuoteBlock,
  QuoteBlockType,
  QuoteDocumentPro,
  QuoteProStatus,
  QuoteTotals,
} from '@/engines/quote/QuoteTypes'

const STATUS_TO_BUDGET: Record<QuoteProStatus, BudgetStatus> = {
  DRAFT: 'DRAFT',
  READY: 'READY',
  SENT: 'SENT',
  APPROVED: 'APPROVED',
  ARCHIVED: 'ARCHIVED',
}

const STATUS_TO_QUOTE: Record<BudgetStatus, QuoteProStatus> = {
  DRAFT: 'DRAFT',
  READY: 'READY',
  SENT: 'SENT',
  APPROVED: 'APPROVED',
  REJECTED: 'ARCHIVED',
  ARCHIVED: 'ARCHIVED',
}

export function mapBlockTypeToLineType(type: QuoteBlockType): BudgetLineType {
  switch (type) {
    case 'product':
      return 'product'
    case 'service':
      return 'service'
    case 'labor':
      return 'labor'
    case 'discount':
      return 'discount'
    case 'note':
    case 'warranty':
    case 'title':
    case 'separator':
    case 'image':
      return 'note'
    case 'subtotal':
      return 'other'
    default:
      return 'other'
  }
}

export function mapLineTypeToBlockType(type: BudgetLineType, metaType?: string): QuoteBlockType {
  if (metaType && isQuoteBlockType(metaType)) return metaType
  switch (type) {
    case 'product':
      return 'product'
    case 'service':
      return 'service'
    case 'labor':
      return 'labor'
    case 'discount':
      return 'discount'
    case 'note':
      return 'note'
    case 'shipping':
      return 'note'
    case 'tax':
      return 'note'
    default:
      return 'note'
  }
}

function isQuoteBlockType(value: string): value is QuoteBlockType {
  return (
    value === 'product' ||
    value === 'service' ||
    value === 'labor' ||
    value === 'note' ||
    value === 'warranty' ||
    value === 'discount' ||
    value === 'subtotal' ||
    value === 'separator' ||
    value === 'title' ||
    value === 'image'
  )
}

export function quoteBlockToBudgetLine(block: QuoteBlock): BudgetLine {
  return {
    id: block.id,
    type: mapBlockTypeToLineType(block.type),
    description: block.description,
    quantity: block.quantity,
    unitCost: block.purchasePrice,
    unitPrice: block.salePrice,
    discountPercent: block.discountPercent,
    discountValue: block.discountValue,
    position: block.position,
    origin: {
      source:
        block.origin.source === 'comparison'
          ? 'comparison'
          : block.origin.source === 'template'
            ? 'template'
            : block.origin.source === 'service_catalog'
              ? 'manual'
              : 'manual',
      productId: block.origin.productId,
      fingerprint: block.origin.fingerprint,
      supplierId: block.origin.supplierId,
      supplierName: block.origin.supplierName,
      externalRef: block.origin.comparisonOfferId,
    },
    meta: {
      quoteBlockType: block.type,
      margin: String(block.margin),
      profit: String(block.profit),
      ...(block.groupId ? { groupId: block.groupId } : {}),
      ...(block.meta ?? {}),
    },
  }
}

export function budgetLineToQuoteBlock(line: BudgetLine): QuoteBlock {
  const type = mapLineTypeToBlockType(line.type, line.meta?.quoteBlockType)
  const gross = line.quantity * line.unitPrice
  const net =
    gross -
    gross * (line.discountPercent / 100) -
    line.discountValue
  const cost = line.quantity * line.unitCost
  const profit = Math.round((net - cost) * 100) / 100
  const margin = net > 0 ? Math.round((profit / net) * 10000) / 100 : 0

  return {
    id: line.id,
    type,
    origin: {
      source:
        line.origin.source === 'comparison'
          ? 'comparison'
          : line.origin.source === 'template'
            ? 'template'
            : 'system',
      productId: line.origin.productId ?? line.id,
      fingerprint: line.origin.fingerprint,
      supplierId: line.origin.supplierId,
      supplierName: line.origin.supplierName,
      comparisonOfferId: line.origin.externalRef,
    },
    description: line.description,
    quantity: line.quantity,
    purchasePrice: line.unitCost,
    salePrice: line.unitPrice,
    margin: Number(line.meta?.margin ?? margin),
    profit: Number(line.meta?.profit ?? profit),
    discountPercent: line.discountPercent,
    discountValue: line.discountValue,
    position: line.position,
    groupId: line.meta?.groupId,
    meta: omitKeys(line.meta, ['quoteBlockType', 'margin', 'profit', 'groupId']),
  }
}

export function quoteTotalsToBudgetTotals(totals: QuoteTotals, shipping = 0, taxes = 0): BudgetTotals {
  return {
    quantity: totals.quantity,
    costTotal: totals.purchaseTotal,
    subtotal: totals.saleTotal,
    discountTotal: totals.discountTotal,
    shipping,
    taxes,
    total: totals.total,
    profit: totals.profitTotal,
    marginPercent: totals.marginPercent,
  }
}

export function budgetTotalsToQuoteTotals(totals: BudgetTotals): QuoteTotals {
  return {
    quantity: totals.quantity,
    purchaseTotal: totals.costTotal,
    saleTotal: totals.subtotal,
    profitTotal: totals.profit,
    marginPercent: totals.marginPercent,
    discountTotal: totals.discountTotal,
    total: totals.total,
  }
}

export function quoteDocumentToBudgetDocument(
  doc: QuoteDocumentPro,
  options?: { shipping?: number; taxRatePercent?: number; number?: string },
): BudgetDocument {
  return {
    id: doc.id,
    number: options?.number ?? `ORC-${doc.id.slice(0, 8)}`,
    title: doc.title,
    status: STATUS_TO_BUDGET[doc.status],
    client: {
      id: doc.customerId,
      name: '',
    },
    lines: [...doc.blocks]
      .sort((a, b) => a.position - b.position)
      .map(quoteBlockToBudgetLine),
    totals: quoteTotalsToBudgetTotals(doc.totals, options?.shipping ?? 0, 0),
    globalDiscountPercent: doc.globalDiscountPercent,
    globalDiscountValue: doc.globalDiscountValue,
    shipping: options?.shipping ?? 0,
    taxRatePercent: options?.taxRatePercent ?? 0,
    notes: '',
    version: 1,
    createdAt: doc.createdAt,
    updatedAt: doc.updatedAt,
  }
}

export function budgetDocumentToQuoteDocument(
  doc: BudgetDocument,
  options?: { globalMarginPercent?: number | null },
): QuoteDocumentPro {
  return {
    id: doc.id,
    customerId: doc.client.id,
    title: doc.title,
    status: STATUS_TO_QUOTE[doc.status],
    globalMarginPercent: options?.globalMarginPercent ?? null,
    globalDiscountPercent: doc.globalDiscountPercent,
    globalDiscountValue: doc.globalDiscountValue,
    blocks: [...doc.lines]
      .sort((a, b) => a.position - b.position)
      .map(budgetLineToQuoteBlock),
    totals: budgetTotalsToQuoteTotals(doc.totals),
    createdAt: doc.createdAt,
    updatedAt: doc.updatedAt,
  }
}

function omitKeys(
  meta: Record<string, string> | undefined,
  keys: string[],
): Record<string, string> | undefined {
  if (!meta) return undefined
  const next: Record<string, string> = {}
  for (const [key, value] of Object.entries(meta)) {
    if (!keys.includes(key)) next[key] = value
  }
  return Object.keys(next).length ? next : undefined
}
